import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { fetchData } from '../../helpers/apiUtils';
import InfoTip from '../../components/InfoTip';

/**
 * Vergleich Welcome-Einladung zur Wiederholung vs. Original-Heat-ups an frische Abonnenten
 * (Plan .claude/plans/2026-09-30-yannick-paket-webinarverteiler-dnv.md § P2). Je 1.000 Personen × Webinar,
 * mit 95-%-Intervall — bei den kleinen Zahlen der Einladung ist das Intervall die eigentliche Aussage.
 */

const API = '/api/webinar-rerun-admin';

interface Quote {
  n: number;
  pro1000: number | null;
  von: number | null;
  bis: number | null;
}

interface Zeile {
  zeile: 'einladung' | 'heatup';
  einheiten: number;
  /** Erster Kontakt jünger als 7 Tage — nicht im Nenner (Final-Review 07.10.). */
  unreif?: number;
  kampagnen: number;
  klick: Quote;
  anmeldung: Quote;
  zuschauer: Quote;
  kauf: Quote;
}

interface Vergleich {
  startDate: string;
  endDate: string;
  tage: number;
  zeilen: Zeile[];
  ungespiegelt: { einladung: number; heatup: number };
}

const ZEILE_LABEL: Record<Zeile['zeile'], string> = {
  einladung: 'Welcome-Einladung (Wiederholung)',
  heatup: 'FT-Heat-ups an Abonnenten ≤ 14 Tage',
};

const SPALTEN: { key: 'klick' | 'anmeldung' | 'zuschauer' | 'kauf'; label: string; tip: string }[] = [
  { key: 'klick', label: 'Klicks', tip: 'Personen mit mindestens einem Klick auf eine der Mails zum Webinar.' },
  { key: 'anmeldung', label: 'Anmeldungen', tip: 'Anmeldung zum beworbenen Webinar (Einladung: zu einer Wiederholung derselben Quelle) innerhalb von 7 Tagen nach der ersten Mail.' },
  { key: 'zuschauer', label: 'Zuschauer', tip: 'Tatsächlich zugeschaut (Wiedergabe im Raum bzw. Live-Präsenz am Original).' },
  { key: 'kauf', label: 'Käufe', tip: 'Gültige Bestellung des Produkts aus dem Webinar innerhalb von 7 Tagen nach der ersten Mail.' },
];

const TH = { backgroundColor: '#e0e0e0 !important', color: 'black', fontWeight: 'bold' } as const;

function zahl(v: number | null, stellen = 1): string {
  return v === null ? '–' : v.toLocaleString('de-DE', { maximumFractionDigits: stellen, minimumFractionDigits: stellen });
}

export default function RerunInviteVsHeatupSection(): React.ReactElement {
  const [data, setData] = useState<Vergleich | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [start, setStart] = useState('');
  const [ende, setEnde] = useState('');

  const laden = useCallback(async (s: string, e: string) => {
    setLoading(true);
    setError(null);
    const q = new URLSearchParams();
    if (s) q.set('startDate', s);
    if (e) q.set('endDate', e);
    const r = await fetchData(`${API}/invite-vs-heatup${q.toString() ? `?${q}` : ''}`);
    if (r?.success) {
      const d = r.data as Vergleich | null;
      setData(d);
      if (d) {
        setStart((alt) => alt || d.startDate);
        setEnde((alt) => alt || d.endDate);
      }
    } else setError(r?.error || 'Vergleich konnte nicht geladen werden');
    setLoading(false);
  }, []);

  useEffect(() => {
    void laden('', '');
  }, [laden]);

  return (
    <Paper sx={{ p: 2, mt: 3, backgroundColor: '#ffffff' }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} mb={1}>
        <Typography variant="h6" sx={{ flexGrow: 1 }}>
          Einladung vs. Heat-up (frische Abonnenten)
        </Typography>
        <TextField size="small" type="date" label="Versand von" value={start} onChange={(e) => setStart(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField size="small" type="date" label="bis" value={ende} onChange={(e) => setEnde(e.target.value)} InputLabelProps={{ shrink: true }} />
        <Button size="small" startIcon={loading ? <CircularProgress size={14} /> : <RefreshIcon />} disabled={loading} onClick={() => laden(start, ende)}>
          Laden
        </Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Je 1.000 erreichte Personen × Webinar, jede Person zählt je Webinar einmal. In Klammern das 95-%-Intervall: überschneiden sich
        die Intervalle, ist ein Unterschied noch nicht belegt. Einladung = frische Welcome-Anmelder (Tag 0–2); Heat-up = Empfänger der
        FT-Heat-ups, deren erster Kontakt höchstens 14 Tage zurückliegt.
      </Typography>
      {error && <Alert severity="warning">{error}</Alert>}
      {data && (data.ungespiegelt.einladung > 0 || data.ungespiegelt.heatup > 0) && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Noch nicht gespiegelt (fehlen im Nenner, kommen mit dem stündlichen Abgleich): {data.ungespiegelt.einladung} Einladungs- und{' '}
          {data.ungespiegelt.heatup} Heat-up-Kampagnen.
        </Alert>
      )}
      {data && (
        <TableContainer component={Paper} sx={{ backgroundColor: '#ffffff' }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={TH}>Kanal</TableCell>
                <TableCell sx={TH} align="right">
                  Personen × Webinar
                </TableCell>
                {SPALTEN.map((s) => (
                  <TableCell key={s.key} sx={TH} align="right">
                    <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                      {s.label} je 1.000 <InfoTip text={s.tip} />
                    </Box>
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.zeilen.map((z) => (
                <TableRow key={z.zeile}>
                  <TableCell>
                    {ZEILE_LABEL[z.zeile]}
                    <Typography variant="caption" display="block" color="text.secondary">
                      {z.kampagnen} Webinar(e)
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    {z.einheiten.toLocaleString('de-DE')}
                    {z.unreif ? (
                      <Typography component="div" variant="caption" color="text.secondary">
                        + {z.unreif.toLocaleString('de-DE')} jünger als 7 Tage
                      </Typography>
                    ) : null}
                  </TableCell>
                  {SPALTEN.map((s) => (
                    <TableCell key={s.key} align="right">
                      {zahl(z[s.key].pro1000)}
                      <Typography variant="caption" display="block" color="text.secondary">
                        {z[s.key].n} · ({zahl(z[s.key].von)}–{zahl(z[s.key].bis)})
                      </Typography>
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
}
